/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.data.repository.cauce.RecurringScheduleRepository
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.data.util.safeThrow
import studio.oddpods.cauce.core.model.session.CreateRecurringScheduleRequest
import studio.oddpods.cauce.core.model.session.RecurringSchedule
import studio.oddpods.cauce.core.model.session.SessionStatus
import studio.oddpods.cauce.core.model.session.UpdateRecurringScheduleRequest
import kotlin.time.Clock

class RecurringScheduleRepositoryImpl(
    private val supabase: SupabaseClient,
) : RecurringScheduleRepository {

    private val allSchedules = MutableStateFlow<List<RecurringSchedule>?>(null)

    override fun getSchedulesForPatient(patientId: String): Flow<List<RecurringSchedule>> =
        allSchedules
            .onStart { if (allSchedules.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> list.filter { it.patientId == patientId } }

    override suspend fun createSchedule(request: CreateRecurringScheduleRequest): Result<RecurringSchedule> =
        safeCall {
            val inserted = supabase.postgrest.from("recurring_schedules").insert(request) {
                select()
            }.decodeSingle<RecurringSchedule>()
            fetchAll()
            inserted
        }

    override suspend fun getSchedule(scheduleId: String): Result<RecurringSchedule> = safeCall {
        allSchedules.value?.find { it.id == scheduleId }
            ?: run {
                fetchAll()
                allSchedules.value?.find { it.id == scheduleId }
            }
            ?: error("Schedule not found: $scheduleId")
    }

    override suspend fun updateSchedule(
        scheduleId: String,
        request: UpdateRecurringScheduleRequest,
    ): Result<RecurringSchedule> = safeCall {
        val updated = supabase.postgrest.from("recurring_schedules").update(request) {
            filter { eq("id", scheduleId) }
            select()
        }.decodeSingle<RecurringSchedule>()
        deleteFutureScheduledSessions(scheduleId).getOrNull()
        fetchAll()
        updated
    }

    override suspend fun deactivateSchedule(scheduleId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("recurring_schedules").update({
            set("is_active", false)
        }) {
            filter { eq("id", scheduleId) }
        }
        deleteFutureScheduledSessions(scheduleId).getOrNull()
        fetchAll()
        Unit
    }

    override suspend fun deleteFutureScheduledSessions(scheduleId: String): Result<Unit> = safeCall {
        val today = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .date
            .toString()
        supabase.postgrest.from("sessions").delete {
            filter {
                eq("schedule_id", scheduleId)
                eq("session_status", SessionStatus.SCHEDULED.name)
                gt("session_date", today)
            }
        }
    }

    private suspend fun fetchAll() = safeThrow {
        allSchedules.value = supabase.postgrest.from("recurring_schedules").select {
            order("created_at", Order.DESCENDING)
        }.decodeList<RecurringSchedule>()
    }
}
